/* eslint-disable react/no-unstable-nested-components */
import type { InvoiceLineItem } from 'payments-api/model';
import type { InvoiceStatus } from 'payments-api/model/invoiceStatus';
import {
  useGetInvoicesIdSuspense,
  useGetProductsSuspense,
  useGetUsersSuspense,
  usePostInvoicesId,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import {
  AddLineItemOperationOp,
  DeleteLineItemOperationOp,
  UpdateLineItemOperationOp,
} from 'payments-api/model';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';

import lilId from '@fragment/lil-id';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { ItemTableItem } from './ItemTable';
import { FormatBillingAmount } from './formatBillingAmount';
import { InvoiceDetailLoading } from './InvoiceDetailLoading';
import { InvoiceHistoryPanel } from './InvoiceHistoryPanel';
import { getTotalsFromItems, InvoiceItemsSection } from './InvoiceItemsSection';
import { InvoiceLayout } from './InvoiceLayout';

type Props = {
  workspaceId: string;
  invoiceId: string;
};

const lineItemsToItemTableItems = (
  lineItems: InvoiceLineItem[] | undefined
): ItemTableItem[] => {
  if (!lineItems) return [];
  return lineItems.map((li) => ({
    id: li.id,
    type: li.type,
    product_id: li.product_id,
    amount: li.amount,
    currencyCode: li.currencyCode,
    description: li.description,
    user_id: li.user_id,
  }));
};

const InvoiceDetailPageContent = ({
  workspaceId,
  invoiceId,
}: Props): JSX.Element => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data } = useGetInvoicesIdSuspense(invoiceId);
  const { data: productsData } = useGetProductsSuspense();
  const { data: usersData } = useGetUsersSuspense();
  const postInvoicesIdMutation = usePostInvoicesId();
  const [, setLocation] = useLocation();
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState<ItemTableItem[]>([]);
  const usersDataList = usersData.data;
  const products = productsData.data;

  const invoice = data.data;

  useDocumentTitle(`Invoice ${invoice.id}`);

  const displayItems = isAdjusting
    ? invoiceItems
    : lineItemsToItemTableItems(invoice.lineItems);

  const handleBackToInvoices = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
    );
  }, [workspaceId, setLocation]);

  const handleStartAdjust = useCallback(() => {
    setInvoiceItems(lineItemsToItemTableItems(invoice.lineItems));
    setIsAdjusting(true);
  }, [invoice.lineItems]);

  const handleAddInvoiceItem = useCallback(
    (item: Omit<ItemTableItem, 'id'>) => {
      setInvoiceItems((prev) => [...prev, { ...item, id: lilId() }]);
    },
    []
  );

  const handleUpdateInvoiceItem = useCallback(
    (id: string, item: Omit<ItemTableItem, 'id'>) => {
      setInvoiceItems((prev) =>
        prev.map((i) => (i.id === id ? { ...item, id } : i))
      );
    },
    []
  );

  const removeItem = useCallback((id: string) => {
    setInvoiceItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleSubmitChanges = useCallback(async () => {
    if (!invoiceId || !invoice.lineItems) return;
    const serverIds = new Set(invoice.lineItems.map((li) => li.id));
    const serverMap = new Map(invoice.lineItems.map((li) => [li.id, li]));

    const removes = invoice.lineItems
      .filter((li) => !invoiceItems.some((i) => i.id === li.id))
      .map((li) => ({
        op: DeleteLineItemOperationOp.delete,
        id: li.id,
      }));

    const updates = invoiceItems
      .filter((i) => serverIds.has(i.id))
      .filter((i) => {
        const server = serverMap.get(i.id);
        return server && String(server.amount) !== String(i.amount);
      })
      .map((i) => ({
        op: UpdateLineItemOperationOp.update,
        id: i.id,
        amount: i.amount,
      }));

    const adds = invoiceItems
      .filter((i) => !serverIds.has(i.id))
      .map((i) => ({
        op: AddLineItemOperationOp.add,
        type: i.type,
        product_id: i.product_id,
        amount: i.amount,
        currency_code: i.currencyCode,
        description: i.description,
        user: { external_id: i.user_id },
      }));

    const lineItemsUpdate = [...removes, ...updates, ...adds];
    if (lineItemsUpdate.length === 0) return;

    try {
      await postInvoicesIdMutation.mutateAsync({
        id: invoiceId,
        data: {
          // Gets around a bug in the API
          version: Math.max(invoice!.version, 1),
          line_items_update: lineItemsUpdate,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['/invoices'] });
      setIsAdjusting(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save changes.';
      showToast(<Toast type="error" message={message} />);
    }
  }, [
    invoiceId,
    invoice,
    invoiceItems,
    postInvoicesIdMutation,
    queryClient,
    showToast,
  ]);

  const handleCancelAdjust = useCallback(() => {
    setIsAdjusting(false);
  }, []);

  const hasChanges = useMemo(() => {
    if (!invoice.lineItems || !isAdjusting) return false;
    const serverIds = new Set(invoice.lineItems.map((li) => li.id));
    const serverMap = new Map(invoice.lineItems.map((li) => [li.id, li]));
    const currentIds = new Set(invoiceItems.map((i) => i.id));
    if (invoice.lineItems.some((li) => !currentIds.has(li.id))) return true;
    if (invoiceItems.some((i) => !serverIds.has(i.id))) return true;
    return invoiceItems.some((i) => {
      const server = serverMap.get(i.id);
      return server && String(server.amount) !== String(i.amount);
    });
  }, [invoice.lineItems, isAdjusting, invoiceItems]);

  const canSubmitAdjustment = hasChanges && !postInvoicesIdMutation.isPending;

  const totalPayinContent = useMemo(() => {
    const { totalAmount } = getTotalsFromItems(displayItems);
    if (totalAmount.length === 0) return null;
    return totalAmount.map(({ amount, currency }) => (
      <FormatBillingAmount
        key={currency}
        amount={amount}
        currencyCode={currency}
      />
    ));
  }, [displayItems]);

  const primaryActionTitle = useMemo(() => {
    if (postInvoicesIdMutation.isPending) return 'Wait...';
    if (isAdjusting) return 'Submit';
    return 'Adjust';
  }, [postInvoicesIdMutation.isPending, isAdjusting]);

  return (
    <>
      <InvoiceLayout
        onBack={handleBackToInvoices}
        title={invoice.id}
        status={invoice.status as InvoiceStatus}
        primaryAction={{
          title: primaryActionTitle,
          'data-testid': 'submit-invoice-button',
          onClick: isAdjusting ? handleSubmitChanges : handleStartAdjust,
          disabled: isAdjusting ? !canSubmitAdjustment : false,
          disabledTooltipText: isAdjusting ? undefined : undefined,
        }}
        secondaryAction={
          isAdjusting
            ? {
                title: 'Cancel',
                'data-testid': 'cancel-invoice-button',
                onClick: handleCancelAdjust,
              }
            : {
                title: showHistory ? 'Close History' : 'History',
                'data-testid': 'history-invoice-button',
                onClick: () => setShowHistory((prev) => !prev),
              }
        }
        issued={invoice.created}
        totalPayinContent={totalPayinContent}
        historyPanel={
          showHistory ? (
            <InvoiceHistoryPanel invoiceId={invoiceId} />
          ) : undefined
        }
      >
        <InvoiceItemsSection
          items={displayItems}
          products={products}
          users={usersDataList}
          onAddItem={isAdjusting ? handleAddInvoiceItem : undefined}
          onUpdateItem={isAdjusting ? handleUpdateInvoiceItem : undefined}
          onRemoveItem={isAdjusting ? removeItem : undefined}
          showUnbalancedWarning={isAdjusting}
          userBalances={invoice.users}
        />
      </InvoiceLayout>
    </>
  );
};

export const InvoiceDetailPage = ({
  workspaceId,
  invoiceId,
}: Props): JSX.Element => (
  <Suspense fallback={<InvoiceDetailLoading />}>
    <InvoiceDetailPageContent workspaceId={workspaceId} invoiceId={invoiceId} />
  </Suspense>
);
